/**
 * TreatX Admin Messaging Panel - V2 Thread-Based Architecture
 * Platform oversight interface for monitoring and intervention
 * 
 * Features:
 * - View ALL threads across the platform
 * - Filter by clinic, thread type, status
 * - Handle escalations with priority
 * - Intervene in conversations
 * - Monitor message health metrics
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MessagingAPI, { 
  THREAD_TYPES, PARTICIPANT_ROLES, MESSAGE_TYPES, 
  DELIVERY_STATUS 
} from '../../services/MessagingAPI';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { 
  ArrowLeft, MessageSquare, Send, Image, Paperclip, FileText,
  CheckCheck, Check, AlertTriangle, Building2, User, Shield,
  Clock, DollarSign, Calendar, ChevronRight, X, Filter,
  MoreVertical, Flag, Users, Inbox, Archive, Bell, Search,
  RefreshCw, ExternalLink, Eye, Activity, BarChart3,
  AlertCircle, ChevronDown, Globe
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

// ========== HELPER FUNCTIONS ==========

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  } else {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
};

const getThreadTypeColor = (type, status) => {
  if (status === 'escalated') return 'bg-red-500';
  switch (type) {
    case 'lead': return 'bg-blue-500';
    case 'offer': return 'bg-emerald-500';
    case 'escalation': return 'bg-orange-500';
    default: return 'bg-slate-500';
  }
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-700 border-green-200';
    case 'escalated': return 'bg-red-100 text-red-700 border-red-200';
    case 'resolved': return 'bg-slate-100 text-slate-600 border-slate-200';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const getRoleBadgeStyle = (role) => {
  switch (role) {
    case 'patient': return 'bg-purple-100 text-purple-700';
    case 'provider_sales_rep': return 'bg-blue-100 text-blue-700';
    case 'provider_admin': return 'bg-indigo-100 text-indigo-700';
    case 'treatx_admin': return 'bg-emerald-100 text-emerald-700';
    case 'provider_coordinator': return 'bg-teal-100 text-teal-700';
    case 'provider_after_sales_rep': return 'bg-cyan-100 text-cyan-700';
    case 'system': return 'bg-slate-100 text-slate-600';
    default: return 'bg-gray-100 text-gray-700';
  }
};

// OF-001D #3 — consume the canonical role label resolver.
import { roleLabel as canonicalRoleLabel } from '../../utils/canonicalRoleLabels';
const getRoleLabel = (role) => (role === 'system' ? 'System' : canonicalRoleLabel(role));

// ========== ADMIN STATS DASHBOARD ==========

const AdminStatsDashboard = ({ threads }) => {
  const totalThreads = threads.length;
  const escalatedThreads = threads.filter(t => t.status === 'escalated').length;
  const activeThreads = threads.filter(t => t.status === 'active').length;
  const resolvedThreads = threads.filter(t => t.status === 'resolved').length;
  const leadThreads = threads.filter(t => t.thread_type === 'lead').length;
  const offerThreads = threads.filter(t => t.thread_type === 'offer').length;
  
  // Group by clinic
  const clinicCounts = threads.reduce((acc, t) => {
    const clinic = t.clinic_id || 'Unknown';
    acc[clinic] = (acc[clinic] || 0) + 1;
    return acc;
  }, {});
  const uniqueClinics = Object.keys(clinicCounts).length;
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 p-4 bg-white border-b">
      <div className="text-center p-3 rounded-lg bg-slate-50">
        <p className="text-2xl font-bold text-slate-800">{totalThreads}</p>
        <p className="text-xs text-slate-500">Total Threads</p>
      </div>
      <div className="text-center p-3 rounded-lg bg-red-50">
        <p className="text-2xl font-bold text-red-600">{escalatedThreads}</p>
        <p className="text-xs text-red-600">Escalated</p>
      </div>
      <div className="text-center p-3 rounded-lg bg-green-50">
        <p className="text-2xl font-bold text-green-600">{activeThreads}</p>
        <p className="text-xs text-green-600">Active</p>
      </div>
      <div className="text-center p-3 rounded-lg bg-slate-50">
        <p className="text-2xl font-bold text-slate-500">{resolvedThreads}</p>
        <p className="text-xs text-slate-500">Resolved</p>
      </div>
      <div className="text-center p-3 rounded-lg bg-blue-50">
        <p className="text-2xl font-bold text-blue-600">{leadThreads}</p>
        <p className="text-xs text-blue-600">Lead Inquiries</p>
      </div>
      <div className="text-center p-3 rounded-lg bg-emerald-50">
        <p className="text-2xl font-bold text-emerald-600">{offerThreads}</p>
        <p className="text-xs text-emerald-600">Offer Discussions</p>
      </div>
      <div className="text-center p-3 rounded-lg bg-purple-50">
        <p className="text-2xl font-bold text-purple-600">{uniqueClinics}</p>
        <p className="text-xs text-purple-600">Active Clinics</p>
      </div>
    </div>
  );
};

// ========== ADMIN THREAD TABLE ROW ==========

const AdminThreadRow = ({ thread, onSelect, isSelected }) => {
  const patientParticipant = thread.participants?.find(p => p.role === 'patient');
  const clinicParticipant = thread.participants?.find(p => ['provider_sales_rep', 'provider_admin'].includes(p.role));
  
  return (
    <tr 
      onClick={onSelect}
      className={`cursor-pointer hover:bg-slate-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
      data-testid={`admin-thread-row-${thread.id}`}
    >
      {/* Status */}
      <td className="px-4 py-3">
        <Badge variant="outline" className={getStatusBadgeColor(thread.status)}>
          {thread.status}
        </Badge>
      </td>
      
      {/* Type */}
      <td className="px-4 py-3">
        <Badge variant="outline" className="capitalize">
          {thread.thread_type}
        </Badge>
      </td>
      
      {/* Patient */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-medium text-sm">
            {patientParticipant?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <span className="font-medium text-sm">{patientParticipant?.name || 'Unknown'}</span>
        </div>
      </td>
      
      {/* Clinic */}
      <td className="px-4 py-3">
        <div className="text-sm">
          <p className="font-medium text-slate-700">{thread.clinic_id || 'N/A'}</p>
          {clinicParticipant && (
            <p className="text-xs text-slate-500">{clinicParticipant.name}</p>
          )}
        </div>
      </td>
      
      {/* Subject */}
      <td className="px-4 py-3 max-w-xs">
        <p className="text-sm text-slate-700 truncate">{thread.subject || 'No subject'}</p>
      </td>
      
      {/* Messages */}
      <td className="px-4 py-3 text-center">
        <span className="text-sm font-medium text-slate-600">{thread.message_count || 0}</span>
      </td>
      
      {/* Last Activity */}
      <td className="px-4 py-3">
        <span className="text-xs text-slate-500">{formatTimestamp(thread.last_message_at)}</span>
      </td>
      
      {/* Actions */}
      <td className="px-4 py-3">
        <Button size="sm" variant="ghost">
          <Eye className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
};

// ========== ADMIN THREAD DETAIL PANEL ==========

const AdminThreadDetailPanel = ({ thread, messages, loading, onClose, onIntervene, onResolve }) => {
  const [showMessages, setShowMessages] = useState(true);
  
  if (!thread) return null;
  
  const patientParticipant = thread.participants?.find(p => p.role === 'patient');
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-slate-900 text-white flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Thread Details</h3>
            <p className="text-slate-300 text-sm">{thread.subject || 'Conversation'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusBadgeColor(thread.status) + ' text-sm'}>
              {thread.status}
            </Badge>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-slate-800">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Thread Info */}
          <div className="p-4 bg-slate-50 border-b">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Patient</p>
                <p className="font-medium text-slate-800">{patientParticipant?.name || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Clinic</p>
                <p className="font-medium text-slate-800">{thread.clinic_id || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Thread Type</p>
                <Badge variant="outline" className="capitalize mt-1">{thread.thread_type}</Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Created</p>
                <p className="text-sm text-slate-700">{formatTimestamp(thread.created_at)}</p>
              </div>
            </div>
            
            {/* Participants */}
            <div className="mt-4">
              <p className="text-xs text-slate-500 uppercase font-medium mb-2">Participants</p>
              <div className="flex flex-wrap gap-2">
                {thread.participants?.map((p, idx) => (
                  <Badge key={idx} className={getRoleBadgeStyle(p.role) + ' text-xs'}>
                    {p.name} ({getRoleLabel(p.role)})
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          
          {/* Messages Section */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={() => setShowMessages(!showMessages)}
                className="flex items-center gap-2 text-sm font-medium text-slate-700"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showMessages ? '' : '-rotate-90'}`} />
                Messages ({messages?.length || 0})
              </button>
            </div>
            
            {showMessages && (
              <div className="space-y-3 max-h-80 overflow-y-auto bg-slate-50 rounded-lg p-4">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
                  </div>
                ) : messages?.length > 0 ? (
                  messages.map((msg, idx) => {
                    const isSystem = msg.message_type === 'system';
                    
                    if (isSystem) {
                      return (
                        <div key={msg.id} className="flex justify-center">
                          <div className="bg-slate-200 rounded-full px-4 py-2 text-xs text-slate-600">
                            {msg.content}
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div key={msg.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 ${getRoleBadgeStyle(msg.sender_role).replace('text-', 'bg-').replace('-100', '-500').replace('-700', '')}`}>
                          {msg.sender_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{msg.sender_name}</span>
                            <Badge className={getRoleBadgeStyle(msg.sender_role) + ' text-xs h-5'}>
                              {getRoleLabel(msg.sender_role)}
                            </Badge>
                            <span className="text-xs text-slate-400">{formatTimestamp(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-700">{msg.content}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center text-slate-500 py-4">No messages yet</p>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Actions Footer */}
        <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            Thread ID: {thread.id?.substring(0, 8)}...
          </div>
          <div className="flex items-center gap-2">
            {thread.status !== 'resolved' && (
              <>
                <Button variant="outline" onClick={onIntervene}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Intervene
                </Button>
                <Button onClick={onResolve} className="bg-green-600 hover:bg-green-700">
                  <CheckCheck className="h-4 w-4 mr-2" />
                  Resolve
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== MAIN COMPONENT ==========

const AdminMessagingPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Get admin from unified auth (single source of truth)
  const adminUser = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('treatx_unified_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  
  // State
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [clinicFilter, setClinicFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const adminId = adminUser?.sub || adminUser?.id || 'admin_treatx';
  const adminName = adminUser?.name || adminUser?.email || 'TreatX Admin';
  
  // Fetch all threads (admin)
  const fetchThreads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await MessagingAPI.getAllThreadsAdmin({
        threadType: typeFilter || undefined,
        status: statusFilter || undefined,
        clinicId: clinicFilter || undefined,
        limit: 100
      });
      setThreads(response.threads || []);
    } catch (error) {
      console.error('Error fetching admin threads:', error);
      toast({
        title: 'Error',
        description: 'Failed to load threads',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, clinicFilter]);
  
  // Fetch messages for selected thread
  const fetchMessages = useCallback(async (threadId) => {
    try {
      setMessagesLoading(true);
      const response = await MessagingAPI.getMessages({ threadId });
      setMessages(response.messages || []);
      
      // Get full thread details
      const threadResponse = await MessagingAPI.getThread(threadId);
      setSelectedThread(threadResponse);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);
  
  // Handle thread selection
  const handleSelectThread = (thread) => {
    setSelectedThread(thread);
    fetchMessages(thread.id);
  };
  
  // Handle admin intervention
  const handleIntervene = async () => {
    if (!selectedThread) return;
    
    const message = prompt('Enter your message to the thread participants:');
    if (!message) return;
    
    try {
      // Add admin as participant if not already
      await MessagingAPI.addParticipant({
        threadId: selectedThread.id,
        userId: adminId,
        role: 'treatx_admin',
        name: adminName
      });
      
      // Send intervention message
      await MessagingAPI.sendMessage({
        threadId: selectedThread.id,
        senderId: adminId,
        senderRole: 'treatx_admin',
        senderName: adminName,
        content: `[TreatX Admin Intervention] ${message}`,
        messageType: 'system'
      });
      
      toast({
        title: 'Intervention Sent',
        description: 'Your message has been added to the thread'
      });
      
      // Refresh messages
      fetchMessages(selectedThread.id);
    } catch (error) {
      console.error('Error intervening:', error);
      toast({
        title: 'Error',
        description: 'Failed to send intervention',
        variant: 'destructive'
      });
    }
  };
  
  // Handle resolve thread
  const handleResolveThread = async () => {
    if (!selectedThread) return;
    
    try {
      await MessagingAPI.resolveThread(selectedThread.id);
      toast({
        title: 'Thread Resolved',
        description: 'Conversation marked as resolved'
      });
      
      setSelectedThread(null);
      fetchThreads();
    } catch (error) {
      console.error('Error resolving thread:', error);
      toast({
        title: 'Error',
        description: 'Failed to resolve thread',
        variant: 'destructive'
      });
    }
  };
  
  // Filter threads
  const filteredThreads = threads.filter(thread => {
    // Tab filter
    if (activeTab === 'escalated' && thread.status !== 'escalated') return false;
    if (activeTab === 'active' && thread.status !== 'active') return false;
    if (activeTab === 'resolved' && thread.status !== 'resolved') return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const patientName = thread.participants?.find(p => p.role === 'patient')?.name?.toLowerCase() || '';
      const clinicId = thread.clinic_id?.toLowerCase() || '';
      const subject = thread.subject?.toLowerCase() || '';
      return patientName.includes(query) || clinicId.includes(query) || subject.includes(query);
    }
    
    return true;
  });
  
  // Get unique clinics for filter
  const uniqueClinics = [...new Set(threads.map(t => t.clinic_id).filter(Boolean))];
  
  // Auth check
  if (!adminUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
            <p className="text-slate-500 mb-4">Please log in to access the admin panel</p>
            <Button onClick={() => navigate('/treatx-admin/login')}>Log In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-messaging-panel">
      {/* Header */}
      <header className="bg-emerald-900 text-white px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/treatx-admin/dashboard')} className="p-2 hover:bg-emerald-800 rounded-full">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Messaging Oversight</h1>
            <p className="text-emerald-200 text-sm">Monitor and manage platform communications</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchThreads}
            className="text-emerald-200 hover:text-white hover:bg-emerald-800"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Badge className="bg-emerald-700 text-emerald-100">
            <Shield className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        </div>
      </header>
      
      {/* Stats Dashboard */}
      <AdminStatsDashboard threads={threads} />
      
      {/* Main Content */}
      <div className="p-6">
        {/* Filters & Tabs */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search patient, clinic, subject..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {/* Clinic Filter */}
            <select
              value={clinicFilter}
              onChange={(e) => setClinicFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">All Clinics</option>
              {uniqueClinics.map(clinic => (
                <option key={clinic} value={clinic}>{clinic}</option>
              ))}
            </select>
            
            {/* Type Filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">All Types</option>
              <option value="lead">Lead</option>
              <option value="offer">Offer</option>
              <option value="escalation">Escalation</option>
            </select>
          </div>
          
          {/* Status Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full max-w-md">
              <TabsTrigger value="all">All ({threads.length})</TabsTrigger>
              <TabsTrigger value="escalated" className="text-red-600">
                Escalated ({threads.filter(t => t.status === 'escalated').length})
              </TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="resolved">Resolved</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        {/* Threads Table */}
        <Card>
          <CardHeader>
            <CardTitle>Communication Threads</CardTitle>
            <CardDescription>
              {filteredThreads.length} thread{filteredThreads.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin h-8 w-8 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto" />
                <p className="text-sm text-slate-500 mt-2">Loading threads...</p>
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="p-12 text-center">
                <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-medium text-slate-600 mb-1">No threads found</h3>
                <p className="text-sm text-slate-500">Adjust your filters or wait for new conversations</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Patient</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Clinic</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Subject</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Msgs</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Last Activity</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredThreads.map(thread => (
                      <AdminThreadRow
                        key={thread.id}
                        thread={thread}
                        onSelect={() => handleSelectThread(thread)}
                        isSelected={selectedThread?.id === thread.id}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Thread Detail Modal */}
      {selectedThread && (
        <AdminThreadDetailPanel
          thread={selectedThread}
          messages={messages}
          loading={messagesLoading}
          onClose={() => setSelectedThread(null)}
          onIntervene={handleIntervene}
          onResolve={handleResolveThread}
        />
      )}
    </div>
  );
};

export default AdminMessagingPanel;
